import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { Truck, MapPin, Package, Trash, CheckCircle, ArrowsClockwise, Printer, NavigationArrow } from "@phosphor-icons/react";

/**
 * AdminPurgeTruckSheet — printable truck-day route sheet.
 * Aggregates every pending purge item into one Google-Maps route link
 * + photo tiles. Driver reviews, taps addresses in order, marks each
 * item as picked up.
 */
export default function AdminPurgeTruckSheet() {
  const [sheet, setSheet] = useState({ stops: 0, items: 0, route_url: "", addresses: [], items_list: [] });
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/purge/truck-sheet");
      setSheet(r.data || {});
    } catch (e) {
      toast.error("Could not load truck sheet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const complete = async (id) => {
    setCompleting(id);
    try {
      await api.post(`/purge/${id}/complete`);
      toast.success("Marked picked up");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not update"));
    } finally {
      setCompleting(null);
    }
  };

  // Group items by address for the sheet
  const grouped = {};
  for (const it of (sheet.items_list || [])) {
    const key = it.property_address || "—";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(it);
  }
  const orderedAddresses = sheet.addresses || [];

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        {/* Header — hidden in print */}
        <div className="mb-6 flex items-center gap-3 print:hidden">
          <div className="h-12 w-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a]">
            <Truck weight="fill" size={26} className="text-white"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-3xl uppercase tracking-tight leading-none">Truck Day · Purge Route</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
              {sheet.stops || 0} stop{sheet.stops === 1 ? "" : "s"} · {sheet.items || 0} pickup{sheet.items === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={load} data-testid="truck-refresh"
                  className="h-10 w-10 border-2 border-slate-900 bg-white flex items-center justify-center hover:bg-slate-100">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
          <button onClick={() => window.print()} data-testid="truck-print"
                  className="h-10 px-4 border-2 border-slate-900 bg-white flex items-center gap-1 hover:bg-slate-100 text-xs font-black uppercase tracking-widest">
            <Printer size={16} weight="bold"/> Print
          </button>
        </div>

        {/* Route CTA — full width, tap-first */}
        {sheet.route_url && (
          <a href={sheet.route_url} target="_blank" rel="noopener noreferrer"
             data-testid="truck-route-btn"
             className="mb-6 print:hidden flex items-center justify-center gap-3 w-full h-16 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-display font-black uppercase tracking-widest text-sm rounded-lg border-2 border-slate-900 shadow-[4px_4px_0px_#0f172a]">
            <NavigationArrow weight="fill" size={22}/>
            Open Route in Google Maps · {sheet.stops || 0} stops
          </a>
        )}

        {loading && (
          <div className="text-center py-12 text-sm font-black uppercase tracking-widest text-slate-500">
            Loading…
          </div>
        )}

        {!loading && orderedAddresses.length === 0 && (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-lg p-12 text-center">
            <Trash weight="light" size={44} className="mx-auto text-slate-300"/>
            <div className="text-lg font-black text-slate-500 mt-3">No pending purges</div>
            <div className="text-xs text-slate-400 mt-1">Techs flag bulk trash from any WO or inspection — flagged items land here for truck day.</div>
          </div>
        )}

        {/* Print-only header */}
        <div className="hidden print:block mb-4 border-b-2 border-slate-900 pb-2">
          <div className="font-display font-black text-2xl uppercase">Truck Day · Purge Route</div>
          <div className="text-xs">
            {sheet.stops || 0} stops · {sheet.items || 0} items · generated {new Date().toLocaleString()}
          </div>
        </div>

        {/* Stops list */}
        <div className="space-y-4">
          {orderedAddresses.map((addr, i) => (
            <div key={addr} data-testid={`truck-stop-${i}`}
                 className="bg-white border-2 border-slate-900 rounded-lg overflow-hidden print:break-inside-avoid">
              <div className="px-4 py-2 bg-amber-100 border-b-2 border-slate-900 flex items-center gap-3">
                <div className="h-8 w-8 bg-amber-500 rounded-full flex items-center justify-center font-black text-slate-900 border-2 border-slate-900">
                  {i + 1}
                </div>
                <MapPin weight="fill" size={18} className="text-amber-700"/>
                <div className="font-display font-black text-base uppercase tracking-tight flex-1">{addr}</div>
                <div className="text-xs font-black text-amber-800">{(grouped[addr] || []).length} pickup{(grouped[addr] || []).length === 1 ? "" : "s"}</div>
              </div>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(grouped[addr] || []).map(it => (
                  <PurgeItemCard key={it.id} it={it} onComplete={() => complete(it.id)}
                                 completing={completing === it.id}/>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}

function PurgeItemCard({ it, onComplete, completing }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  const photoUrl = it.photo_path ? `${API_BASE}/api/files/${encodeURIComponent(it.photo_path)}` : null;
  const kindLabel = it.item_kind === "property" ? "Whole Unit" : "Single Item";
  return (
    <div className="bg-slate-50 border border-slate-300 rounded p-2 flex gap-3" data-testid={`truck-item-${it.id}`}>
      {photoUrl ? (
        <img src={photoUrl} alt="purge" className="w-24 h-24 object-cover rounded border border-slate-300 flex-shrink-0"/>
      ) : (
        <div className="w-24 h-24 bg-slate-200 rounded border border-slate-300 flex items-center justify-center flex-shrink-0">
          <Package size={26} className="text-slate-400"/>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 mb-1">
          <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
            it.item_kind === "property" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
          }`}>
            {kindLabel}
          </span>
          {it.room_name && (
            <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
              {it.room_name}
            </span>
          )}
        </div>
        {it.note && <div className="text-xs text-slate-700 line-clamp-3">{it.note}</div>}
        <div className="text-[10px] text-slate-500 mt-1">
          Flagged by {it.created_by_name || "tech"} · {it.created_at ? new Date(it.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
        </div>
        <button
          onClick={onComplete}
          disabled={completing}
          data-testid={`truck-item-complete-${it.id}`}
          className="mt-1.5 print:hidden h-8 px-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-slate-900 text-[10px] font-black uppercase tracking-widest rounded border-2 border-slate-900 flex items-center gap-1">
          <CheckCircle weight="fill" size={12}/>
          {completing ? "…" : "Picked Up"}
        </button>
      </div>
    </div>
  );
}
